(ns ui.wallet.billing-email
  "The billing email in the payment panel, editable in place: \"Change\"
   opens a field with the current address, Save issues
   `:command/wallet-billing-email` (app.wallet.billing-email), which
   changes the email of the Paddle customer. The draft lives in the store
   under `:wallet/billing-email`; nil means the address is only shown.

   Texts are `[:i18n ...]` references, resolved by `ui.wallet.page`."
  (:require [app.team.validation :as validation]
            [clojure.string :as str]
            [parts.replicant.command :as command]
            [storrito.ui.button :as button]
            [ui.subscription.core :as core]))

(defn save-command
  "`parts.replicant.command` logs the outcome under the full command map,
   so the status and error lookups must use exactly this map."
  [draft]
  {:command/kind :command/wallet-billing-email
   :command/data {:email (str/trim (str draft))}})

(def max-email-length
  ;; Paddle's limit, also checked by app.wallet.billing-email.
  320)

(defn draft-error
  [draft]
  (let [email (str/trim (str draft))]
    (when (or (not (validation/email? email))
              (> (count email) max-email-length))
      [:i18n :billing-email/invalid])))

(defn unchanged?
  [draft email]
  (= (str/lower-case (str/trim (str draft)))
     (str/lower-case (str email))))

(defn command-error
  "A known `:error-code` of app.wallet.billing-email is translated, any
   other error is shown as the server sent it."
  [w save]
  (if (= :billing-email/taken
         (:error-code (command/get-latest-result w save)))
    [:i18n :billing-email/taken]
    (core/command-error w save [:i18n :page/command-failed])))

(defn display
  "Without an email the Paddle customer could not be loaded, so there is
   nothing to change."
  [email]
  [:div.flex.flex-wrap.items-center.gap-x-2
   [:span.break-all email]
   (when-not (str/blank? email)
     [:button {:class ["text-sm" "text-blue-600" "hover:underline"]
               :on {:click [[:store/assoc :wallet/billing-email email]]}}
      [:i18n :billing-email/change]])])

(defn editor
  [w draft email]
  (let [save (save-command draft)
        issued? (command/issued? w save)
        error (draft-error draft)]
    [:div
     [:input
      {:type "email"
       :autocomplete "email"
       :aria-label [:i18n :billing-email/new]
       :class ["w-full" "rounded-md" "border-0" "py-2" "px-3" "text-sm"
               "text-gray-900" "ring-1" "ring-inset" "ring-gray-300"
               "focus:ring-2" "focus:ring-inset" "focus:ring-secondary-500"]
       :value draft
       :on {:input [[:store/assoc :wallet/billing-email :event/target.value]]}}]
     [:div.mt-2.flex.items-center.gap-3
      [:button
       {:class button/button-primary-css
        :disabled (boolean (or issued?
                               error
                               (unchanged? draft email)))
        :on {:click [[:data/command
                      save
                      {:on-success (conj core/query-actions
                                         [:store/dissoc :wallet/billing-email])}]]}}
       (if issued?
         [:i18n :billing-email/saving]
         [:i18n :billing-email/save])]
      [:button {:class ["text-sm" "text-gray-600" "hover:underline"]
                :on {:click [[:store/dissoc :wallet/billing-email]]}}
       [:i18n :billing-email/cancel]]]
     [:p.mt-2.text-xs.text-gray-500 [:i18n :billing-email/hint]]
     ;; The organizations of one user share one Paddle customer (see
     ;; app.part/ensure-customer-id!), the app cannot cheaply tell whether
     ;; this one does, so the hint is always shown:
     [:p.mt-1.text-xs.text-gray-500 [:i18n :billing-email/shared-hint]]
     (when (and error
                (not (str/blank? draft)))
       [:p.mt-2.text-sm.text-red-500 error])
     (when-let [error* (command-error w save)]
       [:p.mt-2.text-sm.text-red-500 error*])]))

(defn billing-email
  [w email]
  (if-let [draft (:wallet/billing-email w)]
    (editor w draft email)
    (display email)))
